import type { fabric } from 'fabric';
import type { TemplateType } from '../template-type';
import type { PlayerNameFormat } from './player-name-format';

export type PlaceholderKind = 'placeholder-image' | 'placeholder-text';

export type PlaceholderImageFitMode = 'contain' | 'cover';

export type PlaceholderTextAlign = 'left' | 'center' | 'right';

export const VISIBLE_WHEN_VALUES = ['winner', 'loser', 'played', 'not_played'] as const;

export type VisibleWhen = (typeof VISIBLE_WHEN_VALUES)[number];

export interface PlaceholderCommonData {
  kind: PlaceholderKind;
  placeholderKey: string;
  placeholderLabel: string;
  teamNumber?: number;
  playerNumber?: number;
  /**
   * 1-based index into `ResolverContext.matches`. Only meaningful for templates
   * that bundle several matches in one canvas (MULTIPLE_SCHEDULE,
   * MULTIPLE_RESULTS). When unset, or `ctx.matches` is empty, the resolver
   * falls back to the top-level `ctx.match` / `ctx.homeTeam` / `ctx.awayTeam`
   * — preserving single-match SCHEDULE / RESULTS behavior.
   */
  matchNumber?: number;
  /**
   * Round of a playoff bracket, counted from the final: 1 = final, 2 = semi-final,
   * 3 = quarter-final… Counting from the final keeps one template valid on a
   * bracket of any size — rounds deeper than the bracket are filtered out like
   * surplus table rows. Only meaningful on PLAYOFF_BRACKET, where `matchNumber`
   * is the pair inside the round and `teamNumber` the side of the pair. The
   * third-place match is `bracketRound: 1, matchNumber: 2`.
   */
  bracketRound?: number;
  /**
   * Show the object only when the condition holds for its bracket pair: `winner`
   * / `loser` — the side `teamNumber` won / lost; `played` / `not_played` — the
   * pair has / has not been played. Unset = always shown. Evaluated by the
   * renderer; the editor only stores it.
   */
  visibleWhen?: VisibleWhen;
  previewMode?: boolean;
  /**
   * Stable per-instance fallback seed used for preview image when placeholderKey
   * is empty. Assigned on first preview render. Editor-only.
   */
  previewSeed?: string;
  /**
   * Runtime-resolved value used by the headless render page. When set, the
   * factory renders this instead of the preview/sample placeholder. Not
   * persisted to board JSON in normal author flows — set just before export.
   */
  resolvedImageUrl?: string;
  resolvedText?: string;
}

export interface PlaceholderImageData extends PlaceholderCommonData {
  kind: 'placeholder-image';
  fitMode: PlaceholderImageFitMode;
  /**
   * Stroke color applied to the placeholder background rect and to the
   * rendered preview/resolved image's outline. When undefined, the factory
   * falls back to the dashed-indigo placeholder cue (or red when invalid).
   */
  stroke?: string;
  /** Stroke width in user-space px. Defaults to the placeholder cue width when `stroke` is unset. */
  strokeWidth?: number;
  /** Group opacity, 0..1. Applied to the whole fabric.Group. */
  opacity?: number;
  /** Single corner radius in user-space px (rx=ry). Defaults to 4. */
  cornerRadius?: number;
  /**
   * Per-corner radii in user-space px. When present, overrides the uniform
   * `cornerRadius` independently for each corner. When absent, every corner
   * uses `cornerRadius` (the "uniform mode"). Order: top-left, top-right,
   * bottom-right, bottom-left.
   *
   * Its absence is a meaningful tri-state ("uniform"), like `stroke` — so it is
   * deliberately NOT materialized by `materializePlaceholderData`.
   */
  cornerRadii?: PlaceholderCornerRadii;
}

export interface PlaceholderCornerRadii {
  tl: number;
  tr: number;
  br: number;
  bl: number;
}

export interface PlaceholderTextData extends PlaceholderCommonData {
  kind: 'placeholder-text';
  sampleText: string;
  fontFamily: string;
  fontWeight: string | number;
  fontStyle?: string;
  textAlign: PlaceholderTextAlign;
  fill: string;
  maxFontSize: number;
  minFontSize: number;
  /**
   * date-fns format string for the date keys. Empty/undefined falls back to the
   * key's default — `dd.MM.yyyy` for `match_date`, `d MMMM` (no year) for
   * `player_birth_date`, see `defaultDateFormatFor`. Ignored by other keys.
   */
  dateFormat?: string;
  /**
   * Template for the combined `score` key. Tokens `t1` and `t2` are replaced
   * with home and away scores respectively. Empty/undefined falls back to
   * the resolver default (`t1:t2`). Ignored by other keys.
   */
  scoreFormat?: string;
  /**
   * Name layout for the `player_name` key. Empty/undefined falls back to the
   * resolver default (Фамилия Имя Отчество), so templates authored before this
   * field existed render exactly as they did. Ignored by other keys.
   */
  nameFormat?: PlayerNameFormat;
  /**
   * Vertical anchoring of the text inside the box. Empty/undefined = `center`,
   * which is how every text placeholder rendered before this field existed.
   * The inspector offers it for `goal_scorers` only.
   */
  verticalAlign?: PlaceholderVerticalAlign;
  /**
   * How a list that does not fit the box is squeezed in. Empty/undefined =
   * `auto`. Only `goal_scorers` reads it.
   */
  overflowMode?: PlaceholderOverflowMode;
  /**
   * Colour of own-goal lines. Empty/undefined = `DEFAULT_OWN_GOAL_FILL` (red).
   * Only `goal_scorers` reads it.
   */
  ownGoalFill?: string;
  /**
   * Whether an own-goal line carries the « (АГ)» mark. Empty/undefined = true.
   * Only `goal_scorers` reads it.
   */
  ownGoalMark?: boolean;
  /**
   * Event kinds left out of the list. Stored as the *hidden* set so a kind
   * added later shows up on old templates without a migration.
   * Empty/undefined = everything shown. Only `goal_scorers` reads it.
   */
  hiddenEventKinds?: GoalScorerEventKind[];
}

export type PlaceholderVerticalAlign = 'top' | 'center' | 'bottom';

/**
 * - `shrink` — smaller font down to `minFontSize`, then cut with «+N»;
 * - `columns` — two columns, then cut with «+N»;
 * - `truncate` — keep the font, cut with «+N»;
 * - `auto` — font → two columns → cut.
 */
export type PlaceholderOverflowMode = 'shrink' | 'columns' | 'truncate' | 'auto';

/**
 * Goal event kinds the `goal_scorers` list can show. `goal` covers GOAL and
 * PASS_AND_GOAL, `penalty` an in-match PENALTY_GOAL, `ownGoal` OWN_GOAL and
 * `shootout` AFTER_MATCH_PENALTY_GOAL.
 */
export type GoalScorerEventKind = 'goal' | 'penalty' | 'ownGoal' | 'shootout';

export type PlaceholderData = PlaceholderImageData | PlaceholderTextData;

export interface PlaceholderGeometry {
  left?: number;
  top?: number;
  width: number;
  height: number;
  angle?: number;
}

export interface AddImagePlaceholderOptions extends Partial<PlaceholderImageData>, PlaceholderGeometry {
  placeholderKey?: string;
  placeholderLabel?: string;
}

export interface AddTextPlaceholderOptions extends Partial<PlaceholderTextData>, PlaceholderGeometry {
  placeholderKey?: string;
  placeholderLabel?: string;
  sampleText?: string;
}

export type ResolvedPlaceholderValue =
  | { type: 'text'; value: string }
  | { type: 'image'; url: string };

export interface PlaceholderFacade {
  addImage(options: AddImagePlaceholderOptions): fabric.Group;
  addText(options: AddTextPlaceholderOptions): fabric.Group;
  refit(target: fabric.Group): void;
  /** Re-create every placeholder group on the canvas with the current context. */
  refitAll(): void;
  /** Set the template type used by the factory's red-highlight check; triggers `refitAll`. */
  setTemplateType(type: TemplateType | undefined): void;
  /** Read-back of the active template type. */
  getTemplateType(): TemplateType | undefined;
  isPlaceholder(target: fabric.Object | undefined | null): target is fabric.Group;
  getKind(target: fabric.Object | undefined | null): PlaceholderKind | undefined;
  /**
   * Replace the placeholder visuals with a resolved real value (text string or
   * image URL). Resolves `true` once the new visuals are on the canvas — for
   * images, after the URL has decoded. Resolves `false` when an image URL
   * fails to decode (network error, non-image response, zero-dimension
   * result) — the placeholder group is left in place on the canvas for the
   * caller to drop, mirroring the "no resolved value" removal the substitution
   * step already applies to a placeholder whose key doesn't resolve at all.
   */
  applyResolved(target: fabric.Group, value: ResolvedPlaceholderValue): Promise<boolean>;
}

export interface PlaceholderPluginOptions {
  defaultFontFamily?: string;
  defaultFontWeight?: string | number;
  defaultTextAlign?: PlaceholderTextAlign;
  defaultFill?: string;
  defaultMinFontSize?: number;
  defaultMaxFontSize?: number;
  defaultFitMode?: PlaceholderImageFitMode;
}
